import * as Toast from "@radix-ui/react-toast";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Owner } from "../form-property/property-types";
import { UserPlus } from "lucide-react";

interface CreateOwnerDialogProps {
  onOwnerCreate: (owner: Owner) => void;
  trigger?: React.ReactNode;
}

export function CreateOwnerDialog({ onOwnerCreate }: CreateOwnerDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [email, setEmail] = useState("");
  const [observation, setObservation] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleCreateOwner() {
    if (!name.trim()) {
      setToastType("error");
      setToastMessage("O nome do proprietário é obrigatório!");
      setToastOpen(true);
      return;
    }
    setLoading(true);
    const newOwner = {
      data: {
        name,
        telephone,
        cell_phone: cellPhone,
        email,
        observation,
      },
    };
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/owners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newOwner),
      });
      if (!response.ok) throw new Error("Falha ao criar o proprietário");
      const newOwnerData = await response.json();
      const createdOwner = newOwnerData.data;
      setOpen(false);
      setName("");
      setTelephone("");
      setCellPhone("");
      setEmail("");
      setObservation("");
      onOwnerCreate(createdOwner);
      setToastType("success");
      setToastMessage("Proprietário criado com sucesso!");
      setToastOpen(true);
    } catch (error: unknown) {
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao criar o proprietário!");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-purple-700">
              <UserPlus className="h-5 w-5" />
              Cadastrar Novo Proprietário
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl bg-white p-6 shadow-xl sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                Criar Proprietário
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Preencha as informações e clique em salvar
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="name" className="mb-1 block">
                  Nome <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome do proprietário"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="telephone" className="mb-1 block">
                  Telefone
                </Label>
                <Input
                  id="telephone"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  placeholder="(99) 9999-9999"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="cellPhone" className="mb-1 block">
                  Celular
                </Label>
                <Input
                  id="cellPhone"
                  value={cellPhone}
                  onChange={(e) => setCellPhone(e.target.value)}
                  placeholder="(99) 99999-9999"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="email" className="mb-1 block">
                  Email
                </Label>
                <Input
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="observation" className="mb-1 block">
                  Observação
                </Label>
                <Textarea
                  id="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Observações sobre o proprietário"
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={handleCreateOwner}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          className={`fixed bottom-0 right-0 m-6 rounded-md p-4 shadow-md ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <Toast.Description>{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-1 top-1" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
